import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { analyticsAttributes, ROVO_CREATE_VIEW_SPACE_SOURCE } from '../../constants';

import { messages } from './messages';

const styles = cssMap({
	container: {
		display: 'flex',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: token('space.100'),
		alignSelf: 'stretch',
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
});

export type TaskCardFooterProps = {
	onCreateSpace: () => void;
	isExecuted?: boolean;
	projectKey?: string;
	isDisabled?: boolean;
	hasBeenActioned: boolean;
	onActioned: () => void;
};

export const TaskCardFooter = ({
	onCreateSpace,
	isExecuted,
	projectKey,
	isDisabled,
	hasBeenActioned,
	onActioned,
}: TaskCardFooterProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleCreateSpace = useCallback(
		(_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			onCreateSpace();
			onActioned();
			sendAnalyticsEvent(
				analyticsEvent.update({
					actionSubjectId: 'jsmSolutionComposerPlanCreateSpaceButton',
					attributes: analyticsAttributes,
				}),
			);
		},
		[onCreateSpace, onActioned, sendAnalyticsEvent],
	);

	const viewSpaceHref =
		isExecuted && projectKey
			? `${window.location.origin}/jira/servicedesk/projects/${projectKey}/getting-started?source=${ROVO_CREATE_VIEW_SPACE_SOURCE}`
			: undefined;

	return (
		<Box xcss={styles.container}>
			{viewSpaceHref ? (
				<LinkButton appearance="primary" href={viewSpaceHref}>
					{formatMessage(messages.viewSpace)}
				</LinkButton>
			) : (
				<Button
					appearance="primary"
					isDisabled={isDisabled || hasBeenActioned}
					onClick={handleCreateSpace}
				>
					{formatMessage(messages.createSpace)}
				</Button>
			)}
		</Box>
	);
};
